import { useCachedResource } from "common-fe";
import {
  deriveClassStatus,
  deriveRideStatus,
  expectedJudgeCountForClass,
  expectedSectionCountForClass,
  isPanelConsensusTest,
} from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useScoreContext } from "../context/ScoreContext";
import { getAll, rides as ridesCache, tests as testsCache } from "../local/db";

const groupSheetsByRide = (sheets) => {
  const map = {};
  for (const s of sheets) (map[s.rideId] ??= []).push(s);
  return map;
};

// #1557 — the earliest ride scheduledTime in a class. `scheduledTime` is an ISO
// string, so a plain string compare IS a time compare (#632) — never parse a
// Date here. Blank/missing times are skipped rather than sorting first.
function earliestRideTime(rides) {
  let best = null;
  for (const r of rides ?? []) {
    const t = (r?.scheduledTime || "").trim();
    if (t && (best === null || t < best)) best = t;
  }
  return best;
}

// #1280 statuses + #1557 earliest-ride-time, both derived from the SAME ride
// rows this hook already holds. The time map exists so the class list can fall
// back to a class's first ride when Class.scheduledTime is absent (84 of 189 on
// the demo show) WITHOUT a second query or a per-class fan-out.
// #1804 — attaches derived `status` (with panel-consensus resolved via each
// ride's OWN testId, never the class's, per #435) to every ride row in
// every class. Extracted so the offline branch's consensus wiring can be
// pinned directly, the same way statusesFrom's classification already is.
export function ridesWithDerivedStatus(list, rowsByClass, sheetsByRide, testsById = {}) {
  const ridesByClass = {};
  for (const c of list) {
    const rows = rowsByClass[c.id] ?? [];
    const expectedJudgeCount = expectedJudgeCountForClass(c);
    const sectionJudge = expectedSectionCountForClass(c) != null;
    ridesByClass[c.id] = rows.map((r) => {
      const consensus = isPanelConsensusTest(testsById[r.testId] ?? null);
      return {
        ...r,
        status: deriveRideStatus(r, sheetsByRide[r.id] ?? [], expectedJudgeCount, {
          sectionJudge,
          consensus,
        }),
      };
    });
  }
  return ridesByClass;
}

function statusesFrom(classList, ridesByClass) {
  const statuses = {};
  const earliestRideTimes = {};
  for (const c of classList) {
    const rides = ridesByClass[c.id] ?? [];
    statuses[c.id] = deriveClassStatus(rides);
    const t = earliestRideTime(rides);
    if (t) earliestRideTimes[c.id] = t;
  }
  return { statuses, earliestRideTimes };
}

/**
 * #1280 — the derived status of each class on the class list, plus (#1557) the
 * earliest ride time in each, as:
 *
 *   { statuses: { [classId]: DERIVED_CLASS_STATUSES },
 *     earliestRideTimes: { [classId]: isoString } }
 *
 * Both come off the SAME ride rows in the SAME two requests — `earliestRideTimes`
 * costs no extra call, which is the whole point of returning it from here rather
 * than letting the class list query per-class times of its own.
 *
 * There is no stored class status (see deriveClassStatus), so this is computed
 * from the classes' rides. Two paths, same contract:
 *
 *   offline / cache-first — rides + score sheets straight out of IndexedDB,
 *     run through deriveRideStatus with the Class's own judge count. Same
 *     derivation useRidesForClass already does per class, just across the
 *     whole list, and it needs no people/horses/tests so it stays cheap.
 *
 *   online — ONE listRides(showId) call for the whole show (card #637, the
 *     ridesByShow GSI) narrowed to the classes on screen, then ONE
 *     POST /rides/status batch (card #544) for the server-derived statuses.
 *     Two requests for the entire screen — never a fan-out per class.
 *
 * `placing` for the PLACED tier comes off the ride rows themselves in both
 * paths; the batch endpoint returns status only.
 */
export function useClassStatuses(showId, classList) {
  const api = useScoreApi();
  const { online } = useScoreContext();
  const list = classList ?? [];
  // Deps key: re-derive when the set of classes on screen changes, without
  // making the array identity itself a dependency (it's rebuilt every render).
  const key = list
    .map((c) => c.id)
    .sort()
    .join(",");

  return useCachedResource(
    async () => {
      if (!showId || !list.length) return undefined;
      const sheetsByRide = groupSheetsByRide(await getAll("scoreSheets"));
      const rowsByClass = {};
      let sawAny = false;
      for (const c of list) {
        const rows = await ridesCache.byClass(c.id);
        if (rows.length) sawAny = true;
        rowsByClass[c.id] = rows;
      }
      // Nothing cached for any class — report "no data" rather than a map of
      // SCHEDULED, which would look identical to the bug this replaced.
      if (!sawAny) return undefined;
      // #1804 — resolve each ride's OWN Test (ride.testId, never the class's,
      // per #435), once per unique id across the WHOLE list, mirroring
      // useRidesForClass's offline path: a panel-consensus ride's one
      // co-signed sheet must read FINAL here too, not COMPLETED.
      const testsById = {};
      const allRows = Object.values(rowsByClass).flat();
      for (const tid of new Set(allRows.map((r) => r.testId).filter(Boolean))) {
        testsById[tid] = (await testsCache.get(tid)) ?? null;
      }
      const ridesByClass = ridesWithDerivedStatus(list, rowsByClass, sheetsByRide, testsById);
      return statusesFrom(list, ridesByClass);
    },
    async () => {
      // #2196 — `undefined`, not a truthy `{statuses:{}, ...}`: `list` starts
      // empty (ClassList mounts before useClassesForShow settles), and this
      // hook is keyed on the class list, so that empty-list pass used to
      // settle here IMMEDIATELY with a real-looking answer. Because
      // useCachedResource never resets its state back to unsettled when the
      // `key` dep changes (#2162), that stale truthy value survived past the
      // list becoming non-empty and masked the SLOW real fetch (show-wide
      // listRides + batchRideStatus) that follows — nextClassLoading read
      // false while the actual status fetch was still in flight. Matches the
      // readLocal guard above, which already returns `undefined` for the
      // same condition.
      if (!showId || !list.length) return undefined;
      const wanted = new Set(list.map((c) => c.id));
      const showRides = unwrap(await api.rides.listRides(undefined, showId)).filter((r) =>
        wanted.has(r.classId),
      );
      if (!showRides.length) return statusesFrom(list, {});

      const statuses = unwrap(
        await api.rides.batchRideStatus({ rideIds: showRides.map((r) => r.id) }),
      );
      const statusById = new Map(statuses.map((s) => [s.rideId, s.status]));

      const ridesByClass = {};
      for (const r of showRides) {
        (ridesByClass[r.classId] ??= []).push({
          ...r,
          // A null status means the batch didn't resolve the ride; fall back to
          // whatever the row stores rather than dropping it from the class.
          status: statusById.get(r.id) ?? r.status,
        });
      }
      return statusesFrom(list, ridesByClass);
    },
    [api, showId, key],
    online,
  );
}
